/**
 * useDocumentMeta — keeps <title> and OpenGraph/Twitter meta tags in sync
 * with the currently-loaded analysis.
 *
 * When a user shares `https://worthify.co.uk/report/G2TCT` on WhatsApp /
 * Twitter / Slack / iMessage, the social-card preview pulls these tags via
 * server-rendered HTML. Because this is a CSR React app, the tags are
 * injected client-side AFTER paint — most crawlers (WhatsApp, Slack,
 * Twitterbot) execute basic JS, so this gives ~70-80% coverage. Full
 * coverage would require SSR / pre-rendering, which is a separate task.
 *
 * Usage in App.js:
 *   useDocumentMeta({ analysis, registration, language });
 */
import { useEffect } from "react";

const VEHICLE_LABEL = (v) =>
  [v?.make, v?.model, v?.year_of_manufacture].filter(Boolean).join(" ").trim() ||
  v?.make || "this car";

const RISK_EMOJI = { HIGH: "🚨", MEDIUM: "⚠️", LOW: "✅" };

const setMeta = (selector, value) => {
  if (!value) return;
  const el = document.querySelector(selector);
  if (el) el.setAttribute("content", value);
};

export const useDocumentMeta = ({ analysis, registration, language }) => {
  useEffect(() => {
    const lang = language || "en";
    document.documentElement.setAttribute("lang", lang);

    if (!analysis) {
      // Restore default homepage meta
      document.title = "Worthify | UK Car MOT History Checker";
      setMeta('meta[property="og:title"]', "Worthify — UK car MOT history & risk check");
      setMeta('meta[property="og:description"]', "Check any UK car's real risk in 30 seconds. Spot hidden issues, repair costs, and your negotiation lever before you buy.");
      setMeta('meta[property="og:url"]', "https://worthify.co.uk");
      setMeta('meta[name="twitter:title"]', "Worthify — UK car MOT history & risk check");
      setMeta('meta[name="twitter:description"]', "Check any UK car's real risk in 30 seconds.");
      return;
    }

    const vehicle = analysis?.vehicle || {};
    const verdict = analysis?.worthify_score?.verdict || analysis?.ai_analysis?.verdict || "";
    const risk = analysis?.worthify_score?.risk_level || analysis?.risk_level || "";
    const label = VEHICLE_LABEL(vehicle);
    const reg = (registration || "").toUpperCase();
    const emoji = RISK_EMOJI[risk] || "🚗";

    const titleParts = [emoji, label, reg && `(${reg})`, verdict && `— ${verdict}`].filter(Boolean);
    const title = `${titleParts.join(" ")} — Worthify`;

    const score = analysis?.worthify_score?.score;
    const simple = (analysis?.simple_explanation || analysis?.ai_analysis?.simple_explanation || "").trim();
    // Description = first sentence of plain-language summary (if present)
    // or a generic line. WhatsApp clamps at ~200 chars.
    let description = simple;
    if (description) {
      const m = description.match(/^[^.!?]*[.!?]/);
      if (m) description = m[0];
    }
    if (!description) {
      description = `${label} — Worthify Score ${score ?? "—"}/100, risk: ${risk || "checking…"}. See the full breakdown.`;
    }
    description = description.length > 200 ? description.slice(0, 197) + "…" : description;

    document.title = title;
    const url = reg ? `https://worthify.co.uk/report/${reg}` : "https://worthify.co.uk";
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:url"]', url);
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', description);
    setMeta('meta[name="description"]', description);
  }, [analysis, registration, language]);
};

export default useDocumentMeta;
